import { debounce } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
// eslint-disable-next-line no-restricted-imports
import Fuse, { FuseResult } from 'fuse.js';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';

import { customerSelector, useGetContactsQuery, useGetRecipientsQuery } from '@/api/v1';
import { Clickable } from '@/components/atoms/Clickable';
import { ContactItem } from '@/components/molecules/ContactItem';
import { SearchBox } from '@/components/molecules/SearchBox';
import { MainScreen } from '@/components/organisms/MainScreen';
import { useTranslator } from '@/hooks/useTranslator';
import { ContactsDrawerContext } from '@/types/contactDrawerContext';
import { Contact } from '@/types/contacts';
import { Customer } from '@/types/customer';
import { Page } from '@/types/page';
import { RecipientResponse } from '@/types/recipient';
import { isEmptyNullOrUndefined } from '@/utility/isEmptyNullOrUndefined';

export const Contacts: React.FC = (): React.ReactElement => {
  const { data: customer = Customer.empty() } = useSelector(customerSelector);
  const { data: recipientsPage = Page.empty<RecipientResponse>() } = useGetRecipientsQuery(
    { beneficiaryId: customer.beneficiaryId },
    {
      skip: isEmptyNullOrUndefined(customer.beneficiaryId),
    },
  );
  const { data: contacts = [] } = useGetContactsQuery(customer.beneficiaryId);
  const { accountId, contactId } = useParams<ContactsDrawerContext>();
  const [searchKeyword, setSearchKeyword] = React.useState('');

  const navigate = useNavigate();
  const tr = useTranslator();

  const fuse = React.useMemo(
    (): Fuse<Contact> =>
      new Fuse(contacts, {
        keys: ['entity.firstName', 'entity.lastName', 'entity.legalEntityName'],
        threshold: 0.3,
      }),
    [contacts],
  );

  const filteredContacts = React.useMemo((): readonly Contact[] => {
    const trimmed = searchKeyword.trim();
    if (trimmed === '') {
      return contacts;
    }

    return fuse.search(trimmed).map((result: FuseResult<Contact>): Contact => result.item);
  }, [contacts, fuse, searchKeyword]);

  const handleSearch = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchKeyword(event.target.value);
  }, []);

  const debouncedHandleSearch = React.useMemo(
    (): ReturnType<typeof debounce> => debounce(handleSearch, 300),
    [handleSearch],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      debouncedHandleSearch.clear();
    };
  }, [debouncedHandleSearch]);

  const ownRecipients = React.useMemo((): readonly RecipientResponse[] => {
    const { content } = recipientsPage;
    return content.filter(
      (recipient: RecipientResponse): boolean => recipient.belongsToBeneficiary,
    );
  }, [recipientsPage]);

  const onAccountClick = React.useCallback(
    (accountId: string): void => {
      navigate(`account/${accountId}/details`);
    },
    [navigate],
  );

  const onRecipientClick = React.useCallback(
    (recipientId: string): void => {
      navigate(`recipient/${recipientId}/details`);
    },
    [navigate],
  );

  const outletContext = React.useMemo(
    (): ContactsDrawerContext => ({ contactId, accountId }),
    [accountId, contactId],
  );

  return (
    <MainScreen title={tr('CONTACTS')}>
      <Box position="absolute" top={0} right={0}>
        <Button variant="contained" component={Link} to="new">
          {tr('ADD_NEW')}
        </Button>
      </Box>
      <Box display="flex" flexDirection="column" gap={3} mt={1}>
        <Paper elevation={1}>
          <Box px={4} py={5}>
            <Box pb={4}>
              <Typography variant="h6">{tr('YOUR_ACCOUNTS')}</Typography>
            </Box>
            <Box sx={styles.accountsGrid}>
              {ownRecipients.map(
                (recipient: RecipientResponse): React.ReactElement => (
                  <Clickable key={recipient.id} data={recipient.id} onClick={onAccountClick}>
                    <ContactItem
                      recipients={[recipient]}
                      title={recipient.individualName ?? recipient.businessName ?? ''}
                    />
                  </Clickable>
                ),
              )}
            </Box>
          </Box>
        </Paper>
        <Paper elevation={1}>
          <Box px={4} py={5}>
            <Box display="flex" alignItems="center" justifyContent="space-between" pb={4}>
              <Typography variant="h6">{tr('RECIPIENTS')}</Typography>
              <SearchBox onChange={debouncedHandleSearch} />
            </Box>
            {filteredContacts.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                {tr('NO_ITEMS_FOUND')}
              </Typography>
            ) : (
              <Box sx={styles.recipientsGrid}>
                {filteredContacts.map(
                  (contact: Contact): React.ReactElement => (
                    <Clickable key={contact.id} data={contact.id} onClick={onRecipientClick}>
                      <ContactItem
                        key={contact.id}
                        recipients={contact.recipients}
                        title={Contact.getName(contact)}
                      />
                    </Clickable>
                  ),
                )}
              </Box>
            )}
          </Box>
        </Paper>
        <Box height={30} />
      </Box>

      {/* For modals */}
      <Outlet context={outletContext} />
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  recipientsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 2,
  },
  accountsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 2,
  },
};
